/**
 * Contratos de aluguel de um imóvel — o histórico de locação. Abrir um contrato marca o imóvel como
 * ALUGADO, registra as partes (locador/locatário/fiador) e finaliza os ocupantes atuais SEM contrato
 * (o imóvel passa a um novo inquilino); os locatários entram como ocupantes ligados ao contrato.
 * Encerrar/rescindir finaliza os ocupantes ligados ao contrato e, sem outro contrato vigente,
 * desliga a flag `alugado`. Regras de negócio no backend (servicos/imoveis); aqui é a operação.
 */
import { BadgeCheck, FileText, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

type Parte = { owner_tipo: string; owner: { tipo: string; id: string; rotulo: string }; papel: string };
type Contrato = {
  id: string;
  valor_aluguel: string | null;
  valor_condominio: string | null;
  valor_iptu: string | null;
  dia_vencimento: number | null;
  prazo_meses: number | null;
  indice_reajuste: string | null;
  tipo_garantia: string | null;
  data_inicio: string | null;
  data_fim_prevista: string | null;
  data_rescisao: string | null;
  situacao: string;
  observacoes: string | null;
  partes: Parte[];
};

const PAPEIS = [
  { valor: 'locador', rotulo: 'Locador' },
  { valor: 'locatario', rotulo: 'Locatário' },
  { valor: 'fiador', rotulo: 'Fiador' },
];
const GARANTIAS = [
  { valor: '', rotulo: '—' },
  { valor: 'nenhuma', rotulo: 'Nenhuma' },
  { valor: 'fianca', rotulo: 'Fiança' },
  { valor: 'caucao', rotulo: 'Caução' },
  { valor: 'seguro_fianca', rotulo: 'Seguro-fiança' },
  { valor: 'titulo_capitalizacao', rotulo: 'Título de capitalização' },
];
const INDICES = [
  { valor: '', rotulo: '—' },
  { valor: 'igpm', rotulo: 'IGP-M' },
  { valor: 'ipca', rotulo: 'IPCA' },
  { valor: 'incc', rotulo: 'INCC' },
  { valor: 'outro', rotulo: 'Outro' },
];
const rotulo = (lista: { valor: string; rotulo: string }[], v: string | null) =>
  lista.find((x) => x.valor === v)?.rotulo ?? v ?? '';

const BUSCA: Record<'pessoa' | 'empresa', ConfigBusca> = {
  pessoa: {
    endpoint: '/cadastro/pessoas',
    rotulo: (p) => String(p.nome ?? ''),
    sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
    placeholder: 'Buscar pessoa por nome ou CPF…',
    camposRapido: [
      { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
      { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
    ],
  },
  empresa: {
    endpoint: '/cadastro/empresas',
    rotulo: (e) => String(e.razao_social ?? e.nome_fantasia ?? ''),
    sub: (e) => (e.cnpj ? formatar(String(e.cnpj), 'cnpj') : null),
    placeholder: 'Buscar empresa por razão social ou CNPJ…',
    camposRapido: [
      { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
      { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
    ],
  },
};

const dataBR = (iso?: string | null) => (iso ? iso.split('-').reverse().join('/') : '');
const reais = (v: string | null) =>
  v ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : null;
const SITUACAO: Record<string, string> = { vigente: 'Vigente', encerrado: 'Encerrado', rescindido: 'Rescindido' };

const CAMPO =
  'h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]';

type ParteNova = { tipoOwner: 'pessoa' | 'empresa'; alvo: ItemBusca; papel: string };

export function PainelContratosAluguel({ imovelId, onMudou }: { imovelId: string; onMudou?: () => void }) {
  const url = `/cadastro/imoveis/${imovelId}/contratos`;
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [carregando, setCarregando] = useState(true);

  // Formulário de abertura.
  const [valor, setValor] = useState('');
  const [valorCond, setValorCond] = useState('');
  const [valorIptu, setValorIptu] = useState('');
  const [diaVenc, setDiaVenc] = useState('');
  const [prazo, setPrazo] = useState('');
  const [indice, setIndice] = useState('');
  const [garantia, setGarantia] = useState('');
  const [inicio, setInicio] = useState('');
  const [fimPrevisto, setFimPrevisto] = useState('');
  const [obs, setObs] = useState('');
  const [partes, setPartes] = useState<ParteNova[]>([]);
  const [salvando, setSalvando] = useState(false);

  // Sub-formulário de parte.
  const [pTipo, setPTipo] = useState<'pessoa' | 'empresa'>('pessoa');
  const [pAlvo, setPAlvo] = useState<ItemBusca | null>(null);
  const [pPapel, setPPapel] = useState('locatario');

  async function carregar() {
    const r = await api.get<Contrato[]>(url);
    setCarregando(false);
    if (r.ok) setContratos(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setValor('');
    setValorCond('');
    setValorIptu('');
    setDiaVenc('');
    setPrazo('');
    setIndice('');
    setGarantia('');
    setInicio('');
    setFimPrevisto('');
    setObs('');
    setPartes([]);
    setPAlvo(null);
    setPPapel('locatario');
    setPTipo('pessoa');
  }

  function adicionarParte() {
    if (!pAlvo) return;
    setPartes((ps) => [...ps, { tipoOwner: pTipo, alvo: pAlvo, papel: pPapel }]);
    setPAlvo(null);
  }

  async function abrir(): Promise<boolean> {
    if (salvando) return false;
    setSalvando(true);
    const num = (s: string) => (s ? s.replace(',', '.') : null);
    const r = await api.post(url, {
      valor_aluguel: num(valor),
      valor_condominio: num(valorCond),
      valor_iptu: num(valorIptu),
      dia_vencimento: diaVenc ? Number(diaVenc) : null,
      prazo_meses: prazo ? Number(prazo) : null,
      indice_reajuste: indice || null,
      tipo_garantia: garantia || null,
      data_inicio: inicio || null,
      data_fim_prevista: fimPrevisto || null,
      observacoes: obs || null,
      partes: partes.map((p) => ({ owner_tipo: p.tipoOwner, owner_id: p.alvo.id, papel: p.papel })),
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Contrato aberto — imóvel marcado como alugado.');
    limparForm();
    void carregar();
    onMudou?.();
    return true;
  }

  async function encerrar(c: Contrato, situacao: 'encerrado' | 'rescindido') {
    const hoje = new Date().toISOString().slice(0, 10);
    const r = await api.post(`/cadastro/imoveis/contratos/${c.id}/encerrar`, {
      data_rescisao: hoje,
      situacao,
    });
    if (!r.ok) return void toast.error(r.erro);
    toast.success(situacao === 'rescindido' ? 'Contrato rescindido.' : 'Contrato encerrado.');
    void carregar();
    onMudou?.();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="size-4 text-accent" />
          Contratos de aluguel
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Abrir um contrato marca o imóvel como alugado e finaliza os ocupantes sem contrato; os
          locatários entram como ocupantes. Encerrar finaliza os ocupantes ligados ao contrato.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, contratos.length === 0, 'Nenhum contrato ainda.') ?? (
          <div className="flex flex-col gap-2">
            {contratos.map((c) => {
              const vigente = c.situacao === 'vigente';
              return (
                <div key={c.id} className="flex flex-col gap-2 rounded-md border border-border px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-col">
                      <span className="text-[15px] text-text">
                        {reais(c.valor_aluguel) ?? 'Sem valor'}
                        {c.prazo_meses ? ` · ${c.prazo_meses} meses` : ''}
                      </span>
                      <span className="tipo-corpo-pequeno text-text-tertiary">
                        {dataBR(c.data_inicio) || '—'}
                        {c.data_rescisao ? ` → ${dataBR(c.data_rescisao)}` : c.data_fim_prevista ? ` → ${dataBR(c.data_fim_prevista)} (previsto)` : ''}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Chip aceso={vigente}>
                        {vigente && <BadgeCheck className="size-3.5" />}
                        {SITUACAO[c.situacao] ?? c.situacao}
                      </Chip>
                      {vigente && (
                        <>
                          <Button variant="fantasma" size="sm" onClick={() => void encerrar(c, 'encerrado')}>
                            Encerrar
                          </Button>
                          <Button
                            variant="fantasma"
                            size="sm"
                            className="text-danger hover:text-danger"
                            onClick={() => void encerrar(c, 'rescindido')}
                          >
                            Rescindir
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.indice_reajuste && <Chip>{rotulo(INDICES, c.indice_reajuste)}</Chip>}
                    {c.tipo_garantia && <Chip>{rotulo(GARANTIAS, c.tipo_garantia)}</Chip>}
                    {c.dia_vencimento && <Chip>vence dia {c.dia_vencimento}</Chip>}
                    {c.partes.map((p, i) => (
                      <Chip key={i}>
                        {rotulo(PAPEIS, p.papel)}: {p.owner.rotulo}
                      </Chip>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo contrato de aluguel"
          descricao="Valores, prazo, garantia e as partes. Abrir marca o imóvel como alugado e finaliza os ocupantes sem contrato."
          rotuloAbrir="Abrir contrato"
          rotuloSalvar="Abrir contrato"
          podeSalvar
          salvando={salvando}
          onSalvar={abrir}
          onFechar={limparForm}
        >
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Aluguel (R$)</span>
              <input type="text" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} className={CAMPO} />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Condomínio (R$)</span>
              <input type="text" inputMode="decimal" value={valorCond} onChange={(e) => setValorCond(e.target.value)} className={CAMPO} />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">IPTU (R$)</span>
              <input type="text" inputMode="decimal" value={valorIptu} onChange={(e) => setValorIptu(e.target.value)} className={CAMPO} />
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Dia de vencimento</span>
              <input type="number" min={1} max={31} value={diaVenc} onChange={(e) => setDiaVenc(e.target.value)} className={CAMPO} />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Prazo (meses)</span>
              <input type="number" min={1} value={prazo} onChange={(e) => setPrazo(e.target.value)} className={CAMPO} />
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Índice de reajuste</span>
              <select value={indice} onChange={(e) => setIndice(e.target.value)} className={CAMPO}>
                {INDICES.map((x) => (
                  <option key={x.valor} value={x.valor}>
                    {x.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Garantia</span>
              <select value={garantia} onChange={(e) => setGarantia(e.target.value)} className={CAMPO}>
                {GARANTIAS.map((x) => (
                  <option key={x.valor} value={x.valor}>
                    {x.rotulo}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Início</span>
              <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={CAMPO} />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Fim previsto</span>
              <input type="date" value={fimPrevisto} onChange={(e) => setFimPrevisto(e.target.value)} className={CAMPO} />
            </label>
          </div>

          {/* Partes do contrato */}
          <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-2 p-3">
            <span className="tipo-rotulo">Partes (locador / locatário / fiador)</span>
            {partes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {partes.map((p, i) => (
                  <Chip key={i}>
                    {rotulo(PAPEIS, p.papel)}: {BUSCA[p.tipoOwner].rotulo(p.alvo)}
                    <button
                      type="button"
                      onClick={() => setPartes((ps) => ps.filter((_, j) => j !== i))}
                      aria-label="Remover parte"
                      className="ml-1"
                    >
                      <X className="size-3" />
                    </button>
                  </Chip>
                ))}
              </div>
            )}
            <div className="flex gap-1 rounded-md bg-surface p-1">
              {(['pessoa', 'empresa'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setPTipo(t);
                    setPAlvo(null);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-[13px] font-medium capitalize transition ${
                    pTipo === t ? 'bg-surface-2 text-text shadow-[var(--shadow-sm)]' : 'text-text-tertiary'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <ComboBuscaEntidade key={pTipo} config={BUSCA[pTipo]} onSelecionar={setPAlvo} />
            <div className="flex gap-2">
              <select value={pPapel} onChange={(e) => setPPapel(e.target.value)} className={`${CAMPO} flex-1`}>
                {PAPEIS.map((p) => (
                  <option key={p.valor} value={p.valor}>
                    {p.rotulo}
                  </option>
                ))}
              </select>
              <Button type="button" variant="secundario" onClick={adicionarParte} disabled={!pAlvo}>
                <Plus className="size-4" /> Adicionar
              </Button>
            </div>
          </div>

          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Observações</span>
            <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} className={`${CAMPO} h-auto py-2`} />
          </label>
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}
